// Builds the Apple Business Connect connect link. This is the ONLY thing in this slice that touches
// the ABC backend, and it does not call it — the link is a deterministic URL that a staff member
// sends to a client. `state` and PKCE are minted server-side when the client follows it, so nothing
// needs per-invocation generation here.
//
// The CMS never talks HTTP to the ABC integration; grant status arrives via Firestore only. See
// `cms/docs/apple-connect-link-design.md`.

/**
 * Mirrors the backend route's own validation (`OAuthRequestService.SALESFORCE_ID_PATTERN`) so the
 * CMS rejects exactly what `GET /accounts/:accountId/oauth` would 400 on, rather than handing a
 * staff member a link that fails when the client clicks it.
 */
const SALESFORCE_ID_PATTERN = /^[A-Za-z0-9]{5,64}$/;

/**
 * Shape-only validation for `accountId`. Deliberately loose: the backend route places no constraint
 * on it, so inventing a stricter one here would reject legitimate ids. This catches paste accidents
 * (whitespace, a whole URL, an empty field) and Firestore document-id violations — nothing more.
 *
 * It CANNOT tell a real account from a fabricated one: `accountId` lives in the dashboard GCP
 * project and `firebase_config.ts` is hardcoded single-project, so no cross-project lookup exists.
 * That gap is why the confirm step echoes back the linked company name.
 */
const isWellFormedAccountId = (accountId: string): boolean => {
  if (!accountId) return false;
  if (accountId.length > 1500) return false;
  if (accountId === "." || accountId === "..") return false;
  return !/[/\s]/.test(accountId);
};

export type ConnectLinkInput = {
  accountId: string;
  salesforceId: string;
};

export type ConnectLinkValidation =
  | { valid: true; input: ConnectLinkInput }
  | { valid: false; accountIdError?: string; salesforceIdError?: string };

/** Trims and validates staff-entered ids. Trimming matters: these values are pasted, not typed. */
export const validateConnectLinkInput = (
  rawAccountId: string,
  rawSalesforceId: string,
): ConnectLinkValidation => {
  const accountId = rawAccountId.trim();
  const salesforceId = rawSalesforceId.trim();

  const accountIdError = isWellFormedAccountId(accountId)
    ? undefined
    : "Enter the DearDoc account ID — no spaces or slashes. Copy it from the account's dashboard URL.";
  const salesforceIdError = SALESFORCE_ID_PATTERN.test(salesforceId)
    ? undefined
    : "Enter the Salesforce ID — 5 to 64 letters and numbers, nothing else.";

  if (accountIdError || salesforceIdError)
    return { valid: false, accountIdError, salesforceIdError };
  return { valid: true, input: { accountId, salesforceId } };
};

/**
 * The deployed `oAuth` function's origin. A public HTTPS endpoint, not a secret.
 *
 * Throws rather than interpolating `undefined` into the URL — the failure mode this guards against
 * is a link that looks plausible and 404s in the client's browser. Same shape as
 * `services/modmed.ts`'s `apiBaseUrl()`, including the trailing-slash strip: the value carries a
 * trailing slash in some environments, which would otherwise build `...oAuth//accounts/...`.
 */
export const oauthBaseUrl = (): string => {
  const url = import.meta.env.VITE_ABC_OAUTH_BASE_URL;
  if (!url) throw new Error("VITE_ABC_OAUTH_BASE_URL is not configured");
  return url.replace(/\/$/, "");
};

/** `<oauth-fn>/accounts/:accountId/oauth?salesforce_id=…` — the link staff send to a client. */
export const buildConnectLinkUrl = ({ accountId, salesforceId }: ConnectLinkInput): string => {
  const query = new URLSearchParams({ salesforce_id: salesforceId });
  return `${oauthBaseUrl()}/accounts/${encodeURIComponent(accountId)}/oauth?${query.toString()}`;
};

/**
 * Copies the link, reporting success as a boolean rather than throwing.
 *
 * `navigator.clipboard` rejects in an insecure context or when the permission is denied, and a
 * silently-failed copy is worse than a visible one — a staff member would send an empty message. The
 * callers surface the failure and the URL stays selectable in the dialog as a manual fallback.
 */
export const copyConnectLink = async (url: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
};
